/**
 * API URL helper for edge function routing.
 *
 * All edge function calls route through CF Workers:
 *   {cfWorkerUrl}/{functionName}
 *
 * EXPO_PUBLIC_CF_WORKER_URL must be set in the environment.
 */

const CF_WORKER_URL = (process.env.EXPO_PUBLIC_CF_WORKER_URL || '').replace(/\/+$/, '');

/**
 * Build the URL for an edge function call.
 *
 * @param functionName - e.g. 'analyze-drama', 'data-proxy', 'clear-history'
 * @returns Full URL to the function
 */
export function getEdgeFunctionUrl(functionName: string): string {
  return `${CF_WORKER_URL}/${functionName}`;
}

/**
 * Build gateway auth headers for edge function calls.
 *
 * Currently a no-op -- CF Workers use Better Auth JWT, no gateway apikey needed.
 * Kept as an extension point for future gateway auth requirements.
 */
export function getGatewayHeaders(): Record<string, string> {
  return {};
}

/**
 * Build standard auth headers for edge function calls.
 *
 * Combines Content-Type, Authorization, gateway headers, Better Auth
 * provider detection, and API versioning into a single helper.
 *
 * @param accessToken - JWT or opaque token from the auth session
 * @returns Headers object ready to spread into a fetch() call
 */
export function getAuthHeaders(accessToken: string): Record<string, string> {
  if (!accessToken) return {};
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`,
    ...getGatewayHeaders(),
    'Accept-Version': 'v2',
  };
}

/**
 * Assert that a fetch response is OK, throwing with a structured error message.
 *
 * Attempts to parse a JSON error body; falls back to the provided message.
 *
 * @param response - The fetch Response to check
 * @param fallbackMessage - Message to use if the response body has no `error` field
 */
export async function assertResponseOk(response: Response, fallbackMessage: string): Promise<void> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorData.error || fallbackMessage);
  }
}
